import { useState, useEffect, useCallback } from 'react';
import { useUser } from '@clerk/clerk-react';
import { getSubscriptionStatus, createCheckoutSession, verifyCheckoutSession, getApiErrorMessage } from '../services/api';
import { trackFunnelEvent } from '../services/funnel';

/**
 * Hook to manage subscription status and checkout flow.
 * Returns subscription state and functions to check status and start checkout.
 */
export function useSubscription() {
  const { isLoaded, isSignedIn, user } = useUser();
  const [isPremium, setIsPremium] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [purchaseVerified, setPurchaseVerified] = useState(false);

  // Check subscription status
  const checkStatus = useCallback(async () => {
    if (!isLoaded || !isSignedIn) {
      setIsPremium(false);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const status = await getSubscriptionStatus();
      setIsPremium(status.is_premium);
      setError(null);
      trackFunnelEvent(
        'subscription_status_loaded',
        { is_premium: Boolean(status.is_premium), status: status.status || null },
        { clerkUserId: user?.id, path: '/gopro' }
      );
    } catch (err) {
      console.error('Failed to check subscription:', err);
      setError(getApiErrorMessage(err));
      setIsPremium(false);
    } finally {
      setLoading(false);
    }
  }, [isLoaded, isSignedIn, user?.id]);

  const clearPurchaseVerified = useCallback(() => {
    setPurchaseVerified(false);
  }, []);

  // Start checkout flow
  const startCheckout = useCallback(async () => {
    if (!isSignedIn) {
      setError('Please sign in first');
      return;
    }

    try {
      const currentUrl = window.location.origin;
      const { checkout_url } = await createCheckoutSession(
        `${currentUrl}/gopro?success=true`,
        `${currentUrl}/gopro?canceled=true`
      );
      trackFunnelEvent('checkout_redirected', { destination: 'stripe' }, { clerkUserId: user?.id, path: '/gopro' });
      
      // Redirect to Stripe Checkout
      window.location.href = checkout_url;
    } catch (err) {
      console.error('Failed to create checkout:', err);
      setError(getApiErrorMessage(err));
      throw err; // Re-throw so handleUpgrade catches it
    }
  }, [isSignedIn, user?.id]);

  // Check status on mount and when user changes
  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // Check for success/cancel URL params
  useEffect(() => {
    if (!isLoaded) return;

    let cancelled = false;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');

    const cleanupUrl = () => {
      window.history.replaceState({}, '', window.location.pathname);
    };

    const handleCheckoutReturn = async () => {
      if (params.get('success') === 'true') {
        trackFunnelEvent(
          'checkout_returned',
          { outcome: 'success_param', has_session_id: Boolean(sessionId) },
          { clerkUserId: user?.id, path: '/gopro' }
        );
        if (isSignedIn && sessionId) {
          try {
            setLoading(true);
            const verification = await verifyCheckoutSession(sessionId);
            if (cancelled) return;

            setIsPremium(verification.is_premium);
            setPurchaseVerified(Boolean(verification.verified));
            setError(null);
            trackFunnelEvent(
              'checkout_verification_result',
              { verified: Boolean(verification.verified), is_premium: Boolean(verification.is_premium) },
              { clerkUserId: user?.id, path: '/gopro' }
            );
          } catch (err) {
            if (cancelled) return;
            console.error('Failed to verify checkout session:', err);
            setError(getApiErrorMessage(err));
            trackFunnelEvent(
              'checkout_verification_failed',
              { detail: getApiErrorMessage(err) },
              { clerkUserId: user?.id, path: '/gopro' }
            );
            await checkStatus();
          } finally {
            if (!cancelled) {
              setLoading(false);
              cleanupUrl();
            }
          }
          return;
        }

        await checkStatus();
        if (!cancelled) cleanupUrl();
      }

      if (params.get('canceled') === 'true' && !cancelled) {
        trackFunnelEvent('checkout_returned', { outcome: 'canceled' }, { clerkUserId: user?.id, path: '/gopro' });
        cleanupUrl();
      }
    };

    handleCheckoutReturn();
    return () => { cancelled = true; };
  }, [checkStatus, isLoaded, isSignedIn, user?.id]);

  return {
    isPremium,
    loading,
    error,
    purchaseVerified,
    clearPurchaseVerified,
    checkStatus,
    startCheckout,
    isSignedIn,
  };
}
